import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AvatarActionsDrawer from './AvatarActionsDrawer';
import avatarDotsIcon from '../../../assets/avatardots-icon.png';
import postIcon from '../../../assets/post-icon.png';

import {
  listAvatarCreations,
  getAvatarCreation,
  renameAvatarCreation,
  deleteAvatarCreation,
  applyAvatarCreation,
} from '../services/avatarApi';

import '../styles/MyCreations.css';



export default function MyCreations({
  onEdit,
  onProfileUpdated,
  onDeleted,
  savedCreation,
  onShareAsPost,
}) {

  const navigate = useNavigate();

  const [creations, setCreations] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(
    savedCreation ? `"${savedCreation.name}" saved.` : ''
  );

  const mounted = useRef(false);
  const requestBusy = useRef(false);

  // Read the latest record so a rename also updates the drawer.
  const selectedCreation =
    creations.find((item) => item._id === selectedId) || null;

  function errorMessage(err) {
    return (
      err.response?.data?.error ||
      err.message ||
      'Something went wrong. Please try again.'
    );
  }

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;

    async function load() {
      setLoading(true);

      try {
        const data = await listAvatarCreations();

        if (cancelled) return;

        const items = savedCreation
          ? [
              savedCreation,
              ...data.creations.filter(
                (item) => item._id !== savedCreation._id
              ),
            ]
          : data.creations;

        setCreations(items);
        setCursor(data.nextCursor);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [savedCreation]);

  async function runAction(action) {
    if (requestBusy.current) return;

    requestBusy.current = true;
    setBusy(true);
    setError('');
    setMessage('');

    try {
      await action();
    } catch (err) {
      if (mounted.current) {
        setError(errorMessage(err));
      }
    } finally {
      requestBusy.current = false;

      if (mounted.current) {
        setBusy(false);
      }
    }
  }

  function openDrawer(item) {
    if (requestBusy.current) return;

    setError('');
    setSelectedId(item._id);
  }

  function closeDrawer() {
    if (requestBusy.current) return;

    setSelectedId(null);
    setError('');
  }

  function loadMore() {
    runAction(async () => {
      const data = await listAvatarCreations({ cursor });

      if (!mounted.current) return;

      setCreations((current) => {
        const existing = new Set(
          current.map((item) => item._id)
        );

        return [
          ...current,
          ...data.creations.filter(
            (item) => !existing.has(item._id)
          ),
        ];
      });

      setCursor(data.nextCursor);
    });
  }

  function edit(item) {
    runAction(async () => {
      const fresh = await getAvatarCreation(item._id);

      if (!mounted.current) return;

      setSelectedId(null);
      onEdit(fresh);
    });
  }

  function rename(item) {
    const name = window.prompt('Avatar name:', item.name);
    if (name === null) return;

    runAction(async () => {
      const updated = await renameAvatarCreation(item._id, {
        name,
        revision: item.revision,
      });

      if (!mounted.current) return;

      setCreations((current) =>
        current.map((entry) =>
          entry._id === updated._id ? updated : entry
        )
      );

      setSelectedId(null);
      setMessage('Avatar renamed.');
    });
  }

  function remove(item) {
    if (
      !window.confirm(
        `Delete "${item.name}"? Your current profile picture will stay visible.`
      )
    ) {
      return;
    }

    runAction(async () => {
      await deleteAvatarCreation(item._id);

      if (!mounted.current) return;

      setSelectedId(null);

      setCreations((current) =>
        current.filter((entry) => entry._id !== item._id)
      );

      onDeleted?.(item._id);
      setMessage('Creation deleted.');
    });
  }

  function apply(item) {
    if (typeof onProfileUpdated !== 'function') return;

    runAction(async () => {
      setApplyingId(item._id);

      try {
        const data = await applyAvatarCreation(item._id);

        if (!mounted.current) return;

        await onProfileUpdated(data.user);

        if (mounted.current) {
          setSelectedId(null);
          navigate('/profile');
        }
      } finally {
        if (mounted.current) {
          setApplyingId(null);
        }
      }
    });
  }

  return (
    <section
      className="my-creations"
      aria-label="My saved avatars"
    >
      <div className="my-creations__intro">
        <h2>My Creations</h2>
        <p>Your saved avatars, ready to edit or use.</p>
      </div>

      {message && <p role="status">{message}</p>}

      {error && !selectedCreation && (
        <p className="my-creations__error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p role="status">Loading your avatars…</p>
      ) : (
        <>
          {!creations.length && !error && (
            <p>
              No saved avatars yet. Customize one and tap Done
              to save it.
            </p>
          )}

          <div className="my-creations__grid">
            {creations.map((item) => (
              <article
                className="my-creations__card"
                key={item._id}
              >
                <div className="my-creations__preview">
                  <img
                    className="my-creations__image"
                    src={item.imageUrl}
                    alt={item.name}
                    width="512"
                    height="512"
                    loading="lazy"
                  />

                  <button
                    type="button"
                    className="my-creations__dots"
                    aria-label={`Actions for ${item.name}`}
                    aria-haspopup="dialog"
                    aria-expanded={selectedId === item._id}
                    disabled={busy}
                    onClick={() => openDrawer(item)}
                  >
                    <img
                      src={avatarDotsIcon}
                      alt=""
                      width="20"
                      height="20"
                    />
                  </button>
                </div>

                <h3>{item.name}</h3>
              </article>
            ))}
          </div>

          {cursor && (
            <button
              type="button"
              className="my-creations__more"
              disabled={busy}
              onClick={loadMore}
            >
              {busy ? 'Please wait…' : 'Load more'}
            </button>
          )}

          {error && !selectedCreation && (
            <button
              type="button"
              className="my-creations__more"
              disabled={busy}
              onClick={() => window.location.reload()}
            >
              Reload creations
            </button>
          )}
        </>
      )}

      {!loading &&
        creations.length > 0 &&
        !selectedCreation &&
        typeof onShareAsPost === 'function' && (
          <button
            type="button"
            className="my-creations__post-fab"
            aria-label="Select avatars to share as a post"
            title="Share avatars as a post"
            disabled={busy}
            onClick={() => {
              if (requestBusy.current) return;
              onShareAsPost();
            }}
          >
            <img
              src={postIcon}
              alt=""
              width="26"
              height="26"
            />
          </button>
        )}


      <AvatarActionsDrawer
        creation={selectedCreation}
        busy={busy}
        applying={applyingId === selectedCreation?._id}
        canApply={typeof onProfileUpdated === 'function'}
        error={error}
        onClose={closeDrawer}
        onApply={apply}
        onEdit={edit}
        onRename={rename}
        onDelete={remove}
      />
    </section>
  );
}
